import { DropdownMenu } from 'radix-ui';
import { useTranslation } from 'react-i18next';

import { DROPDOWN_MENU_ITEM_CLASS as ITEM_CLASS } from '../components/menuStyles.ts';
import { ACCENTS, ACCENT_COLORS, isAccent, type Accent } from './palette.ts';
import { COLOR_MODES, isColorMode, type ColorMode } from './preference.ts';
import { useTheme } from './themeContext.ts';

/** Translation-key lookups, not display text — `web/src/locales/{en,es}/appearance.json` holds the
 * actual strings. Kept separate from `palette.ts`'s own `ACCENT_LABELS` (still the English-only
 * source that other, non-localized call sites read) rather than editing that file: this component
 * owns its own translated names, `palette.ts` owns the colour system. */
const MODE_LABEL_KEY: Record<ColorMode, string> = {
  system: 'colourMode.system',
  light: 'colourMode.light',
  dark: 'colourMode.dark',
};

const MODE_NAME_KEY: Record<'light' | 'dark', string> = {
  light: 'modeName.light',
  dark: 'modeName.dark',
};

const ACCENT_LABEL_KEY: Record<Accent, string> = {
  blue: 'accent.blue',
  teal: 'accent.teal',
  violet: 'accent.violet',
  amber: 'accent.amber',
  rose: 'accent.rose',
  green: 'accent.green',
};

/** Radix `RadioGroup.value` must be a string, and a practice's brand colour is an arbitrary hex, not
 * a member of `Accent` — so the "use my practice's colour" option needs its own sentinel value that
 * can never collide with a real `Accent` name. `ACCENTS` is a closed lowercase-word enum
 * (`palette.ts`), so a value containing characters no accent name could ever contain is enough. */
const ORG_ACCENT_VALUE = '__org__';

/**
 * Colour mode and accent, in one menu.
 *
 * Radix `DropdownMenu` rather than a hand-rolled popover: it supplies the roving focus, the
 * escape/outside-click dismissal, and the `menuitemradio` semantics that a `<div>` with a click
 * handler does not. `asChild` is deliberately avoided throughout — `docs/comparison-emrx.md` §7
 * records that a Radix `asChild` child missing a forwarded ref positions the floating layer at
 * `translate(0, -200%)` with no error at all, and not using it removes the hazard rather than
 * remembering it.
 *
 * Both `onValueChange` handlers re-validate the string they are given. Radix hands back whatever
 * `value` was on the item, and the allowlist is the only thing standing between a stored
 * preference and an attribute on `<html>`.
 *
 * **The accent section is org-aware (`BACKLOG.md` B4).** Three shapes, driven by `orgBranding`:
 *
 *   - No practice brand colour at all → unchanged from before: the six-name radio group, nothing
 *     extra.
 *   - A brand colour, and the practice allows the override (`allowUserColorPicker: true`) → the
 *     radio group gains a leading "practice colour" option, previewed with the practice's own raw
 *     hex rather than a `--p-accent-*` token (there is no token for an arbitrary colour to read).
 *   - A brand colour, and the practice forbids the override → no picker at all, only a static note
 *     naming the practice as the source — offering a control that would always be refused is worse
 *     than not offering one.
 */
export function ThemeMenu() {
  const { t } = useTranslation('appearance');
  const {
    preference,
    resolvedMode,
    orgBranding,
    resolvedAccentSource,
    setColorMode,
    setAccent,
    deferToOrgAccent,
  } = useTheme();

  const orgColor = orgBranding?.brandColor ?? null;
  const allowPicker = orgBranding?.allowUserColorPicker ?? true;
  const accentGroupValue = resolvedAccentSource === 'org' ? ORG_ACCENT_VALUE : preference.accent;
  const accentSwatchColor =
    resolvedAccentSource === 'org' && orgColor !== null
      ? orgColor
      : ACCENT_COLORS[preference.accent][resolvedMode].solid;
  const accentAriaName =
    resolvedAccentSource === 'org' ? t('accent.orgOption') : t(ACCENT_LABEL_KEY[preference.accent]);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger
        className="inline-flex items-center gap-2 rounded-control border border-line bg-surface px-3 py-2 text-sm font-medium text-ink hover:bg-raised"
        // The visible label is short; the accessible name states the whole current setting, so a
        // screen-reader user does not have to open the menu to learn what is selected.
        aria-label={`${t('trigger')}: ${t(MODE_LABEL_KEY[preference.mode])}${
          preference.mode === 'system'
            ? ` ${t('currentlyNote', { mode: t(MODE_NAME_KEY[resolvedMode]) })}`
            : ''
        }, ${accentAriaName} ${t('accentSuffix')}`}
      >
        <span
          aria-hidden="true"
          className="size-3 rounded-pill ring-1 ring-line-strong"
          style={{ backgroundColor: accentSwatchColor }}
        />
        {t('trigger')}
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          sideOffset={6}
          align="end"
          className="z-50 min-w-56 rounded-panel border border-line bg-surface p-1 text-ink shadow-overlay"
        >
          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            {t('colourMode.groupLabel')}
          </DropdownMenu.Label>
          <DropdownMenu.RadioGroup
            value={preference.mode}
            onValueChange={(value) => {
              if (isColorMode(value)) setColorMode(value);
            }}
          >
            {COLOR_MODES.map((mode) => (
              <DropdownMenu.RadioItem key={mode} value={mode} className={ITEM_CLASS}>
                <span aria-hidden="true" className="w-4 text-accent-text">
                  <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                </span>
                {t(MODE_LABEL_KEY[mode])}
                {mode === 'system' ? (
                  <span className="ml-auto text-xs text-ink-muted">
                    {t('nowActive', { mode: t(MODE_NAME_KEY[resolvedMode]) })}
                  </span>
                ) : null}
              </DropdownMenu.RadioItem>
            ))}
          </DropdownMenu.RadioGroup>

          <DropdownMenu.Separator className="my-1 h-px bg-line" />

          <DropdownMenu.Label className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted">
            {t('accent.groupLabel')}
          </DropdownMenu.Label>
          {orgColor !== null && !allowPicker ? (
            <p className="m-0 px-2 py-1.5 text-sm text-ink-muted">{t('accent.setByPractice')}</p>
          ) : (
            <DropdownMenu.RadioGroup
              value={accentGroupValue}
              onValueChange={(value) => {
                if (value === ORG_ACCENT_VALUE) {
                  deferToOrgAccent();
                } else if (isAccent(value)) {
                  setAccent(value);
                }
              }}
            >
              {orgColor !== null ? (
                <DropdownMenu.RadioItem value={ORG_ACCENT_VALUE} className={ITEM_CLASS}>
                  <span aria-hidden="true" className="w-4 text-accent-text">
                    <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                  </span>
                  <span
                    aria-hidden="true"
                    style={{ backgroundColor: orgColor }}
                    className="size-3 rounded-pill ring-1 ring-line-strong"
                  />
                  {t('accent.orgOption')}
                </DropdownMenu.RadioItem>
              ) : null}
              {ACCENTS.map((accent) => (
                <DropdownMenu.RadioItem key={accent} value={accent} className={ITEM_CLASS}>
                  <span aria-hidden="true" className="w-4 text-accent-text">
                    <DropdownMenu.ItemIndicator>✓</DropdownMenu.ItemIndicator>
                  </span>
                  {/* The swatch is decorative: the name beside it is what carries the meaning, so a
                      user who cannot distinguish the hues can still tell the options apart.

                      This is the one component that names colours directly, and it has to: the
                      `--p-accent-*` primitives are scoped to `:root[data-accent='…']`, so a nested
                      element cannot preview an accent that is not the current one by using a token.
                      `emrx` exempts its own token gallery from the tier boundary for the same
                      reason. Everything else in this app goes through tier 2. */}
                  <span
                    aria-hidden="true"
                    style={{ backgroundColor: ACCENT_COLORS[accent][resolvedMode].solid }}
                    className="size-3 rounded-pill ring-1 ring-line-strong"
                  />
                  {t(ACCENT_LABEL_KEY[accent])}
                </DropdownMenu.RadioItem>
              ))}
            </DropdownMenu.RadioGroup>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * The visible consequence of a rejected theme.
 *
 * `no-console` is an error repo-wide because a console call is a PHI-egress path around the
 * logger's redaction (`eslint.config.js`), so `emrx`'s `console.error` on rollback is not
 * available to us — and it would be the wrong choice anyway. A theme that was silently discarded
 * looks identical to a control that does not work. Also covers a practice's own brand colour
 * failing its own contrast check (`ThemeProvider.tsx`'s `applyAccentAware`) — the same visible
 * notice, the same generic copy, since "a colour choice failed contrast and was not applied" reads
 * correctly whether that colour came from this member or from their practice.
 */
export function ThemeRejectionNotice() {
  const { t } = useTranslation('appearance');
  const { rejection, dismissRejection } = useTheme();
  if (rejection === null) return null;

  return (
    <div
      role="alert"
      className="mb-4 flex flex-col gap-2 rounded-panel border border-danger bg-surface p-3 text-sm text-ink sm:flex-row sm:items-start sm:justify-between"
    >
      <p className="m-0">
        <span className="font-semibold">{t('rejection.title')}</span> {t('rejection.detail')}{' '}
        {rejection}
      </p>
      <button
        type="button"
        onClick={dismissRejection}
        className="self-start rounded-control border border-line px-2 py-1 text-sm font-medium hover:bg-raised"
      >
        {t('rejection.dismiss')}
      </button>
    </div>
  );
}
